/**
 * Theme JavaScript foundation.
 *
 * Loaded once, deferred, on every page. It provides the shared behaviour that component
 * scripts build on and does not itself render anything.
 *
 * Utilities are exposed on a single `window.Hubcart` namespace rather than as ES module
 * exports. Modules would require an import map of Liquid-rendered asset URLs in the
 * document head; a namespace keeps every component script a plain deferred file with no
 * build step and no load-order coupling, which is the constraint this theme is under.
 */

(function () {
  'use strict';

  /** Matches the tier boundaries declared in `snippets/css-variables.liquid`. */
  const BREAKPOINTS = {
    tablet: 768,
    desktop: 1024,
    large: 1280,
  };

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * Elements that can hold focus. Used by the focus trap and by drawers restoring
   * focus on close. `:not([disabled])` and the tabindex guard keep out elements that
   * are present but not reachable.
   */
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'details > summary',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function focusableWithin(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );
  }

  /**
   * Confine Tab to `container` until the returned function is called.
   *
   * Every overlay in this theme — cart drawer, search drawer, mobile menu, quick view,
   * filter panel — is required to use this. An overlay a keyboard user can Tab out of
   * behind the scrim is not usable, and no component should hand-roll its own version.
   */
  function trapFocus(container, initialFocus) {
    const previouslyFocused = document.activeElement;

    function onKeydown(event) {
      if (event.key !== 'Tab') return;

      const focusable = focusableWithin(container);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    (initialFocus || focusableWithin(container)[0] || container).focus();

    return function releaseFocus() {
      container.removeEventListener('keydown', onKeydown);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }

  /** Debounce, for resize and input handlers. */
  function debounce(fn, wait) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /** True when the viewport is at or above the named tier. */
  function isAtLeast(tier) {
    return window.matchMedia(`(min-width: ${BREAKPOINTS[tier]}px)`).matches;
  }

  /**
   * Reveal-on-scroll.
   *
   * Elements marked `[data-reveal]` are hidden by CSS only while the `js` class is on
   * <html>, so a JavaScript failure leaves content visible rather than blank. Each
   * element is unobserved once revealed — the animation is one-way by design.
   *
   * The variants, the timing and the stagger are all in CSS; this only decides *when*
   * an element reveals and *how far down the queue* it sits.
   */

  /** Written as a duration so the merchant's motion toggle and reduced motion zero it. */
  function scaledDelay(ms) {
    return `calc(${ms}ms * var(--motion-scale) * var(--motion-factor))`;
  }

  function inDocumentOrder(a, b) {
    const position = a.target.compareDocumentPosition(b.target);
    return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  }

  function initScrollReveal(root) {
    const targets = (root || document).querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (targets.length === 0) return;

    if (prefersReducedMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    /**
     * Everything crossing the threshold in one callback is, by definition, arriving
     * together — a heading and the paragraph under it, or two sibling blocks caught by
     * the same scroll. Delaying each a step further than the last separates them instead
     * of letting them land as one lump, and it costs no markup: an element arriving alone
     * gets index 0 and no delay, which is the case for almost everything on a page
     * scrolled at reading speed.
     *
     * The reference has no equivalent — every delay it applies is an author-set
     * attribute. This is a generalisation of that, not a departure from it: the step is
     * the reference's own 30ms, and where it names a sequence explicitly (the collection
     * banner's 250 and 500) the attribute wins and this never runs.
     */
    const observer = new IntersectionObserver(
      (entries) => {
        const arriving = entries.filter((entry) => entry.isIntersecting);
        if (arriving.length === 0) return;

        arriving.sort(inDocumentOrder).forEach((entry, index) => {
          const el = entry.target;
          // An author-set delay is a considered position in a sequence; never overrule it.
          if (index > 0 && !el.hasAttribute('data-reveal-delay')) {
            el.style.setProperty(
              '--reveal-delay',
              `calc(var(--duration-reveal-stagger) * ${index})`
            );
          }
          el.classList.add('is-revealed');
          observer.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.01 }
    );

    targets.forEach((el) => {
      const delay = parseInt(el.getAttribute('data-reveal-delay'), 10);
      if (!Number.isNaN(delay)) el.style.setProperty('--reveal-delay', scaledDelay(delay));
      observer.observe(el);
    });
  }

  /**
   * Heading reveal, word by word.
   *
   * `[data-split-words]` replaces `[data-reveal]` on an element rather than joining it:
   * the words are what animate, and animating the heading as well would move them twice.
   *
   * Each word is wrapped in a clipping box and slides up from inside it, so the line
   * assembles rather than fading in as a block. Splitting happens in JavaScript, which
   * means the served markup is a plain heading — the text is readable, selectable and
   * indexable whether or not this runs.
   */

  /** Between one word and the next. Short enough to read as one gesture, not a queue. */
  const WORD_DELAY_STEP = 30;

  function splitIntoWords(element, baseDelay) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    let index = 0;

    textNodes.forEach((node) => {
      if (node.textContent.trim() === '') return;

      const fragment = document.createDocumentFragment();

      // Splitting on a captured group keeps the whitespace, which has to survive as real
      // text nodes or the browser loses every line-break opportunity in the heading.
      node.textContent.split(/(\s+)/).forEach((part) => {
        if (part === '') return;
        if (part.trim() === '') {
          fragment.append(part);
          return;
        }

        const word = document.createElement('span');
        word.className = 'word';

        const inner = document.createElement('span');
        inner.className = 'word__inner';
        inner.setAttribute('data-reveal', '');
        inner.setAttribute('data-reveal-delay', String(baseDelay + index * WORD_DELAY_STEP));
        inner.append(part);

        word.append(inner);
        fragment.append(word);
        index += 1;
      });

      node.replaceWith(fragment);
    });

    return index;
  }

  function initSplitWords(root) {
    // Nothing to gain from rebuilding the heading when the reveal will not play.
    if (prefersReducedMotion.matches || !('IntersectionObserver' in window)) return;

    (root || document)
      .querySelectorAll('[data-split-words]:not([data-split-bound])')
      .forEach((element) => {
        element.dataset.splitBound = '';
        // The attribute's value is the delay before the first word, which is how a banner
        // holds its heading back until the image behind it has settled.
        const baseDelay = parseInt(element.getAttribute('data-split-words'), 10);
        splitIntoWords(element, Number.isNaN(baseDelay) ? 0 : baseDelay);
      });
  }

  /**
   * Button chrome — the hover fill and the busy state.
   *
   * Both are attached here rather than rendered by each section, so that any `.button`
   * anywhere in the theme behaves the same without a section author having to remember
   * the markup. It also keeps the loader out of the server response: a button that is
   * never pressed never gains the extra nodes.
   */
  const FILL_PARKED = '-76%';
  const FILL_ENTRY = '76%';
  const FILL_COVER = '0%';

  /** Pointer hover only. Touch has no hover, and a stuck fill after a tap looks broken. */
  const canHover = window.matchMedia('(any-hover: hover)');

  /**
   * The fill travels one way: in from below on enter, out through the top on leave. A
   * transition alone can only retrace its path, so entry is a two-step — park the fill
   * below with transitions suppressed, then release it. Both positions are off-button,
   * so the teleport is invisible.
   */
  function fillIn(button) {
    button.classList.add('is-fill-reset');
    button.style.setProperty('--button-fill-y', FILL_ENTRY);
    // Forces the style to be committed. Without it the two writes coalesce into one and
    // the fill simply appears at its destination.
    void button.offsetWidth;
    button.classList.remove('is-fill-reset');
    button.style.setProperty('--button-fill-y', FILL_COVER);
  }

  function fillOut(button) {
    button.style.setProperty('--button-fill-y', FILL_PARKED);
  }

  function initButtonFill(root) {
    if (!canHover.matches) return;

    root.querySelectorAll('.button:not([data-fill-bound])').forEach((button) => {
      button.dataset.fillBound = '';

      button.addEventListener('pointerenter', (event) => {
        // A pen or mouse driving a touchscreen still reports hover; a finger does not.
        if (event.pointerType === 'touch' || button.disabled) return;
        fillIn(button);
      });

      button.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        fillOut(button);
      });
    });
  }

  /**
   * Busy state.
   *
   * A submit button marks itself busy when its form submits and clears on `pageshow`,
   * which covers the back-forward cache restoring a page whose form had been sent.
   * Asynchronous callers — add-to-cart, cart updates — set `aria-busy` themselves; this
   * only guarantees the loader exists for them to reveal.
   */
  function initButtonBusy(root) {
    root.querySelectorAll('.button:not([data-busy-bound])').forEach((button) => {
      button.dataset.busyBound = '';

      // Bare text in a button is not an element, so it cannot be faded independently of
      // the loader. Wrapping each text node in place keeps the flex layout identical.
      Array.from(button.childNodes).forEach((node) => {
        if (node.nodeType !== Node.TEXT_NODE || node.textContent.trim() === '') return;
        const wrapper = document.createElement('span');
        wrapper.className = 'button__text';
        node.replaceWith(wrapper);
        wrapper.append(node);
      });

      const loader = document.createElement('span');
      loader.className = 'button__loader';
      loader.setAttribute('aria-hidden', 'true');
      loader.append(
        document.createElement('span'),
        document.createElement('span'),
        document.createElement('span')
      );
      button.append(loader);

      if (button.type !== 'submit' || !button.form) return;

      button.form.addEventListener('submit', () => button.setAttribute('aria-busy', 'true'));
    });
  }

  window.addEventListener('pageshow', () => {
    document
      .querySelectorAll('.button[aria-busy="true"]')
      .forEach((button) => button.removeAttribute('aria-busy'));
  });

  /**
   * Section lifecycle.
   *
   * The theme editor adds, removes and reorders sections without a page load. Anything
   * initialised on DOMContentLoaded must therefore also run when a section is re-rendered,
   * or the merchant sees a section that looks broken until they refresh — which is the
   * most common way a theme fails its editor review.
   */
  function initSections(root) {
    initSplitWords(root);
    initScrollReveal(root);
    initButtonFill(root);
    initButtonBusy(root);
    document.dispatchEvent(new CustomEvent('hubcart:sections:load', { detail: { root } }));
  }

  document.addEventListener('shopify:section:load', (event) => initSections(event.target));
  document.addEventListener('shopify:section:reorder', () => initSections(document));

  function init() {
    initSections(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /**
   * Merged rather than assigned. `layout/theme.liquid` writes the Liquid-rendered routes
   * onto the same namespace from an inline script in <head>, which runs before this
   * deferred file; a plain assignment here would delete it.
   */
  window.Hubcart = Object.assign(window.Hubcart || {}, {
    BREAKPOINTS,
    FOCUSABLE,
    debounce,
    focusableWithin,
    isAtLeast,
    prefersReducedMotion,
    trapFocus,
  });
})();
